import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { isNull, isUndefined } from 'lodash';
import validator from 'validator';

export function sortCodeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null; // No error if the field is empty
    }

    const sortCodePattern = /^\d{2}-\d{2}-\d{2}$/;
    const isValidFormat = sortCodePattern.test(value);

    // Remove non-digit characters (like dashes), then check if it's only zeros
    const digitsOnly = value.replace(/\D/g, '');
    const isOnlyZeros = /^0+$/.test(digitsOnly);
    const isTooLong = digitsOnly.length > 6;

    if (!isValidFormat || isOnlyZeros || isTooLong) {
      return { invalidSortCode: { value } };
    }

    return null;
  };
}

export function customEmailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const email = control.value;
    if (!email) {
      return null; // Skip validation if the field is empty
    }

    // Stricter email regex to allow valid special characters in the local part
    const strictEmailRegex = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9-]+\.[a-zA-Z]{2,15}$/;

    return strictEmailRegex.test(email)
      ? null
      : { invalidEmail: 'Invalid email format' };
  };
}

export function ibanValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isNull(control.value) || isUndefined(control.value) || control.value === '') return null;

    const isValid = validator.isIBAN(control.value);
    const message = {
      invalidIban: { message: 'Invalid IBAN' },
    };
    return isValid ? null : message;
  };
}

export function specialCharacterValidator(allowedRegex: RegExp): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value;
    if (!value) return null;

    for (const char of value) {
      if (!allowedRegex.test(char)) {
        // return { specialCharacterError: true };
        return { specialCharacterError: { allowed: getAllowedCharactersMessage(allowedRegex) } };
      }
    }

    return null;
  };
}

export function getAllowedCharactersMessage(allowedRegex: RegExp): string {
  const pattern = allowedRegex.source;

  // Match characters inside the square brackets, e.g. A-Za-z0-9\-_*
  const match = pattern.match(/\[([^\]]+)\]/);
  if (!match) return 'Allowed characters are not defined.';

  const charClass = match[1];
  const descriptions: string[] = [];

  // Match known ranges and characters
  if (charClass.includes('A-Z')) descriptions.push('uppercase letters (A-Z)');
  if (charClass.includes('a-z')) descriptions.push('lowercase letters (a-z)');
  if (charClass.includes('0-9')) descriptions.push('digits (0-9)');

  // Check for individual characters
  const specials: { [key: string]: string } = {
    '\\-': 'dash (-)',
    '-': 'dash (-)',
    _: 'underscore (_)',
    '\\.': 'dot (.)',
    '\\s': 'space',
    '*': 'asterisk (*)',
    '@': 'at (@)',
    '#': 'hash (#)',
    ',': 'comma (,)',
  };

  // Loop over each character (after ranges)
  for (const char of charClass.replace(/(A-Z|a-z|0-9)/g, '')) {
    const description = specials[char] || specials[`\\${char}`];
    if (description && !descriptions.includes(description)) {
      descriptions.push(description);
    }
  }

  // Final readable output
  return `Only ${descriptions.join(', ').replace(/, ([^,]*)$/, ' and $1')} are allowed.`;
}

export const VALIDATOR_ONLY_SPECIAL_CHARACTERS = Validators.pattern('.*[A-Za-z0-9].*');

export const VALIDATOR_ONLY_SPACES = Validators.pattern('.*\\S.*');

/**
 * Custom validator that ensures expected date is not before issued date
 * @returns ValidatorFn that validates expectedDate >= issuedDate
 */
export function expectedDateAfterIssuedDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const expectedDate = control.value;
    if (!expectedDate) {
      return null; // Let required validator handle empty values
    }

    const formGroup = control.parent;
    if (!formGroup) {
      return null; // No parent form group
    }

    const issuedDate = formGroup.get('issuedDate')?.value;
    if (!issuedDate) {
      return null; // Let issuedDate required validator handle this
    }

    const expected = new Date(expectedDate);
    const issued = new Date(issuedDate);

    // Reset time components for date-only comparison
    expected.setHours(0, 0, 0, 0);
    issued.setHours(0, 0, 0, 0);

    return expected >= issued ? null : { expectedDateBeforeIssued: { expectedDate, issuedDate } };
  };
}

export function swiftBicValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (isNull(control.value) || isUndefined(control.value) || control.value === '') return null;

    const isValid = validator.isBIC(control.value);
    const message = {
      invalidSwiftBic: { message: 'Invalid Swift/BIC' },
    };
    return isValid ? null : message;
  };
}
